import React from "react";
import { ArrowRight, ShieldCheck, Flame, Truck } from "lucide-react";
import { CIMA, openWhatsapp } from "@/lib/cima";

const HERO_BG =
  "https://images.unsplash.com/photo-1564182998523-6923112e7d6b?auto=format&fit=crop&w=1920&q=80";
const HERO_FG =
  "https://customer-assets.emergentagent.com/job_cima-industrial/artifacts/odihsxhs_image.png";

export default function Hero({ onOpenQuote }) {
  return (
    <section
      data-testid="hero-section"
      className="relative pt-12 lg:pt-14 pb-16 lg:pb-24 overflow-hidden bg-white"
    >
      {/* Atmospheric background */}
      <div className="absolute inset-0 -z-10">
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(20%)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white via-white/95 to-white" />
        {/* corner stripe */}
        <div className="absolute -top-10 -right-10 w-40 h-40 stripe-accent opacity-40 rotate-12" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left copy */}
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-slate-300 bg-white text-xs font-mono-cima uppercase tracking-widest text-slate-600 mb-6">
              <span className="w-1.5 h-1.5 bg-[var(--cima-red)]" />
              Zihuatanejo, Gro · México
            </div>

            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[var(--cima-navy)]">
              Soluciones <span className="text-[var(--cima-red)]">Industriales</span> y{" "}
              <span className="relative inline-block">
                Médicas
                <span className="absolute -bottom-1 left-0 w-full h-1 bg-[var(--cima-orange)]" />
              </span>{" "}
              de Alta Calidad
            </h1>

            <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
              Distribución segura y confiable de gases industriales, medicinales y equipos
              especializados. Atención profesional para industria, hospitales, talleres y
              negocios en la Costa Grande de Guerrero.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                data-testid="hero-quote-button"
                onClick={onOpenQuote}
                className="btn-cima-red px-7 py-3.5 text-sm font-semibold uppercase tracking-wide flex items-center justify-center gap-2 group"
              >
                Solicitar Cotización
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                data-testid="hero-whatsapp-button"
                href={`https://wa.me/${CIMA.whatsappIntl}`}
                onClick={(e) => {
                  e.preventDefault();
                  openWhatsapp(
                    "Hola CIMA, vi su sitio web y me interesa solicitar una cotización."
                  );
                }}
                target="_blank"
                rel="noreferrer noopener"
                className="btn-cima-whatsapp px-7 py-3.5 text-sm font-semibold uppercase tracking-wide flex items-center justify-center gap-2"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                  <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.554-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.689-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.762 5.617L1.95 21.255l3.704-1.062zM17.42 14.5c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.151-.174.2-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.371-.272.298-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
                </svg>
                WhatsApp
              </a>
            </div>

            {/* Trust chips */}
            <div className="mt-10 grid grid-cols-3 max-w-xl gap-4">
              {[
                { icon: ShieldCheck, label: "Calidad certificada" },
                { icon: Flame, label: "Gases industriales y médicos" },
                { icon: Truck, label: "Entrega a domicilio" },
              ].map((item, i) => (
                <div
                  key={item.label}
                  data-testid={`hero-trust-${i}`}
                  className="flex flex-col items-start gap-2 border-l-2 border-[var(--cima-orange)] pl-3"
                >
                  <item.icon className="w-5 h-5 text-[var(--cima-red)]" />
                  <span className="text-xs font-medium text-slate-700 leading-tight">
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right visual */}
          <div className="lg:col-span-5 animate-fade-up delay-200">
            <div className="relative">
              {/* Frame */}
              <div className="relative aspect-[4/5] w-full max-w-md mx-auto border border-slate-300 metallic-surface p-3">
                <div
                  className="w-full h-full bg-cover bg-center grain-overlay"
                  style={{ backgroundImage: `url(${HERO_FG})` }}
                />
                {/* Floating badge */}
                <div className="absolute -bottom-5 -left-5 bg-[var(--cima-navy)] text-white px-5 py-4 max-w-[220px] shadow-2xl">
                  <div className="text-[10px] font-mono-cima uppercase tracking-widest text-[var(--cima-orange-soft)]">
                    Atención profesional
                  </div>
                  <div className="font-display font-bold text-lg leading-tight mt-1">
                    {CIMA.hoursFull}
                  </div>
                </div>
                {/* Floating number tag */}
                <div className="absolute -top-4 -right-4 bg-[var(--cima-red)] text-white px-4 py-2">
                  <div className="text-[10px] font-mono-cima uppercase tracking-widest">Llama hoy</div>
                  <a
                    href={`tel:${CIMA.phoneTel}`}
                    className="font-display font-bold text-base"
                    data-testid="hero-phone-link"
                  >
                    {CIMA.phone}
                  </a>
                </div>
              </div>
              {/* Decorative blocks */}
              <div className="absolute -z-10 top-8 right-0 w-32 h-32 stripe-accent opacity-50" />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom strip - marquee-like industrial tag */}
      <div className="mt-16 border-y border-slate-200 bg-[var(--cima-surface)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center justify-between gap-4 text-xs font-mono-cima uppercase tracking-widest text-slate-500">
          <span>Oxígeno · Nitrógeno · Argón · CO₂ · Mezclas Soldadura</span>
          <span className="hidden md:inline">Reguladores · Flujómetros · Renta de Cilindros</span>
          <span>Atención técnica especializada</span>
        </div>
      </div>
    </section>
  );
}
